import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ActivityEventType,
	type ActivityItemEdge,
	type ActivityProduct,
	buildAggQuery,
	is1PActivity,
	isDocumentActivity,
	isGraphActivity,
	isRemoteLinkActivity,
	type QuickFindRecentActivitiesQueryVariables,
	recentActivitiesDocumentDataQuery,
	recentActivitiesQuery,
} from '@atlassian/search-client';
import { type ExperimentConfig } from '@atlassian/search-experiment';

import type { RecentActivities } from '../types';

export const INCLUDED_PRODUCTS = ['confluence', 'jira', 'townsquare', 'graph'];

export const INCLUDED_OBJECT_TYPES = [
	'page',
	'blogpost',
	'whiteboard',
	'issue',
	'project',
	'goal',
	'content',
	'document',
	'remote-link',
];

const ORS_BATCH_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';
const GRANTED = 'granted';
const RECENT_ACTIVITIES_LIMIT = 200;
const OK_STATUS = 200;

/**
 * Returns the list of products to be included in the recent activities query based on the experiment.
 * We do NOT manipulate INCLUDED_OBJECT_TYPES to keep the logic simple.
 *
 * https://switcheroo.atlassian.com/ui/gates/0cd2bdec-6ec9-49e4-9393-54e282cf472a/key/remove-jira-recents-from-quick-find-in-confluence
 */
const getIncludedProductsBasedOnCohort = (): string[] => {
	const COHORTS = {
		removeJira: 'remove-jira', // Test 1.
		confluenceOnly: 'confluence-only', // Test 2.
		control: 'control', // Control
	};

	const removeJiraRecentsExperimentCohort = FeatureGates.getExperimentValue<string>(
		'remove-jira-recents-from-quick-find-in-confluence',
		'cohort',
		'control',

		// We need to know the cohort to load the correct products, but we are NOT triggering the exposure
		// event yet since it would be too wide. Instead, we're doing that in src/ui/quick-find/dialog/index.tsx.
		// For the decision see https://hello.atlassian.net/wiki/spaces/SEARCH/pages/5615125165/Experiment+structure#Options.
		{ fireExperimentExposure: false },
	);

	if (removeJiraRecentsExperimentCohort === COHORTS.removeJira) {
		return INCLUDED_PRODUCTS.filter((product) => product !== 'jira');
	} else if (removeJiraRecentsExperimentCohort === COHORTS.confluenceOnly) {
		return ['confluence'];
	}

	return INCLUDED_PRODUCTS;
};

export const fetchRecentActivities = async (
	cloudId: string,
	aggAbsoluteUrl: string | undefined,
	experimentConfig?: ExperimentConfig,
	after: string = '',
) => {
	const includedProductsBasedOnCohort = getIncludedProductsBasedOnCohort();
	const recentActivitiesProductsFromCombinedExperiment =
		experimentConfig?.combinedExperiment?.enabled &&
		experimentConfig?.combinedExperiment?.uiFeatures?.recentActivitiesProducts;

	const overrideFilteredProducts =
		Array.isArray(recentActivitiesProductsFromCombinedExperiment) &&
		recentActivitiesProductsFromCombinedExperiment.every((p) => INCLUDED_PRODUCTS.includes(p))
			? recentActivitiesProductsFromCombinedExperiment
			: undefined;
	const variables: QuickFindRecentActivitiesQueryVariables = {
		filteredProducts: overrideFilteredProducts || includedProductsBasedOnCohort,
		filteredObjectTypes: INCLUDED_OBJECT_TYPES,
		filteredRootContainerIds: [
			`ari:cloud:platform::site/${cloudId}`,
			`ari:cloud:townsquare::site/${cloudId}`,
		],
		itemsLimit: 200,
		after,
		includeJiraIssueTypeAvatar: fg('rovo_search_jira_icons'),
		includeJiraIssueMetadata: fg('rovo_qf_jira_recents_metadata'),
	};
	return await recentActivitiesQuery({
		variables,
		aggAbsoluteUrl,
	}).then((response) => {
		const edges = response.data?.activity?.myActivity?.all?.edges || [];
		return edges.map(mapEssentialData);
	});
};

export const addGithubResultsToRecentActivities = async (
	recentActivities: RecentActivities,
	aggAbsoluteUrl: string | undefined,
) => {
	const edges = recentActivities.result.map((item) => item.data);
	const aris = getARIStringsFromDocumentData(edges);
	const emptyVariables: QuickFindRecentActivitiesQueryVariables = {
		filteredProducts: [],
		filteredObjectTypes: [],
		filteredRootContainerIds: [],
		itemsLimit: 0, // variables object not used
	};
	const documentData = await fetchDocumentDataInBatches(aris, emptyVariables, aggAbsoluteUrl, 8);

	// Build timestamp map
	const githubLinkTimestampMap = buildTimestampMapFromDocumentData(edges, documentData);
	const githubLinks = scrapeGithubLinksFromDocumentData(documentData);
	const githubMetadataResponse = await objectResolverServiceBatchCall(githubLinks);

	const githubEdges: ActivityItemEdge[] = [];
	addHydratedMetadataToEdges(githubEdges, githubMetadataResponse, githubLinkTimestampMap);
	return githubEdges.map(mapEssentialData);
};

export const fetchDocumentDataInBatches = async (
	aris: string[],
	variables: QuickFindRecentActivitiesQueryVariables,
	aggAbsoluteUrl: string | undefined,
	batchSize: number,
) => {
	const results = {};

	for (let i = 0; i < aris.length; i += batchSize) {
		const batchAris = aris.slice(i, i + batchSize);
		const query = buildAggQuery(batchAris, i);

		try {
			const batchResult = await recentActivitiesDocumentDataQuery(query, variables, aggAbsoluteUrl);
			if (batchResult?.data) {
				Object.assign(results, batchResult.data);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(`Batch ${i}-${i + batchSize} failed:`, error);
		}
	}

	// Merge all batch results
	return results;
};

export const mapEssentialData = (result: ActivityItemEdge) => {
	const product = result.node.object.product;
	let title = '';
	const timestamp = result.node?.event?.timestamp;

	if (is1PActivity(result.node.object)) {
		if (product === 'confluence') {
			title = result.node.object.data?.title || '';
		} else if (product === 'jira') {
			const key = result.node.object.data?.key;
			const summary = result.node.object.data?.fieldsById?.edges?.[0]?.node?.text;
			title = key && summary ? `${key}: ${summary}` : '';
		} else if (product === 'townsquare') {
			title = result.node.object.data?.name || '';
		}
	} else if (
		isGraphActivity(result.node.object) &&
		(isDocumentActivity(result.node.object) || isRemoteLinkActivity(result.node.object))
	) {
		title = result.node.object.data?.displayName || '';
	} else {
		title = '';
	}

	return {
		id: result.node.object.id,
		title,
		data: result,
		type: 'recent-activity' as const,
		activityTimestamp: timestamp,
	};
};

export const getARIStringsFromDocumentData = (edges: ActivityItemEdge[]) => {
	let aris: string[] = [];
	edges.forEach((item) => {
		const ari = item.node.object.id;
		if (ari && (isConfluenceResult(ari) || isJiraResult(ari))) {
			aris.push(ari);
		}
	});
	return aris;
};

export const scrapeGithubLinksFromDocumentData = (documentData: any) => {
	const githubLinks: string[] = [];
	const githubRegex =
		/https:\/\/github\.com\/[\w\-\.]+\/[\w\-\.]+(?:\/(?:issues\/\d+|pull\/\d+|commit\/[a-f0-9]+|blob\/[\w\-\.\/]+|tree\/[\w\-\.\/]+))?(?=\s|$|[^\w\-\/\.])/gi;

	function recurse(value: any) {
		if (typeof value === 'string') {
			// Try to parse JSON strings (especially atlasDocFormat.value)
			if (value.startsWith('{')) {
				try {
					const parsed = JSON.parse(value);
					recurse(parsed); // Recurse into the parsed JSON
					return;
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error('Failed to parse JSON string:', error);
				}
			}

			// Find all GitHub links in the string
			const matches = value.match(githubRegex);
			if (matches) {
				matches.forEach((link) => githubLinks.push(link));
			}
		} else if (typeof value === 'object' && value !== null) {
			Object.values(value).forEach(recurse);
		}
	}

	recurse(documentData);
	return githubLinks;
};

export const buildTimestampMapFromDocumentData = (
	edges: ActivityItemEdge[],
	documentData: any,
): Map<string, string> => {
	const githubLinkTimestampMap = new Map<string, string>();

	// Build the map by correlating document ARIs with their timestamps from edges
	Object.entries(documentData).forEach(([docKey, doc]: [string, any]) => {
		if (doc?.id) {
			// Find the corresponding edge for this document
			const correspondingEdge = edges.find((edge) => edge.node.object.id === doc.id);
			if (correspondingEdge) {
				const timestamp = correspondingEdge.node.event.timestamp;

				// Extract GitHub links from this specific document
				const githubLinks = scrapeGithubLinksFromSingleDocument(doc);
				githubLinks.forEach((link) => {
					githubLinkTimestampMap.set(link, timestamp);
				});
			}
		}
	});
	return githubLinkTimestampMap;
};

export const scrapeGithubLinksFromSingleDocument = (document: any): string[] => {
	const githubLinks: string[] = [];
	const githubRegex =
		/https:\/\/github\.com\/[\w\-\.]+\/[\w\-\.]+(?:\/(?:issues\/\d+|pull\/\d+|commit\/[a-f0-9]+|blob\/[\w\-\.\/]+|tree\/[\w\-\.\/]+))?(?=\s|$|[^\w\-\/\.])/gi;

	function recurse(value: any) {
		if (typeof value === 'string') {
			if (value.startsWith('{')) {
				try {
					const parsed = JSON.parse(value);
					recurse(parsed);
					return;
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error('Failed to parse JSON string:', error);
				}
			}

			const matches = value.match(githubRegex);
			if (matches) {
				matches.forEach((link) => githubLinks.push(link));
			}
		} else if (typeof value === 'object' && value !== null) {
			Object.values(value).forEach(recurse);
		}
	}

	recurse(document);
	return githubLinks;
};

export const objectResolverServiceBatchCall = async (requestData: any) => {
	// Transform URLs to ORS format
	const orsRequestData = requestData.map((item: any) => ({
		resourceUrl: typeof item === 'string' ? item : item.resourceUrl || item.url,
	}));

	return await fetch(ORS_BATCH_ENDPOINT, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(orsRequestData),
	})
		.then(async (response: Response) => {
			if (response === null || !response.ok) {
				// eslint-disable-next-line no-console
				console.error('ORS API call failed:', response.status, response.statusText);
				return [];
			}
			return response.json();
		})
		.catch((error) => {
			// eslint-disable-next-line no-console
			console.error('ORS API call error:', error);
			return [];
		});
};

export const addHydratedMetadataToEdges = (
	edges: ActivityItemEdge[],
	githubMetadataResponse: any,
	githubLinkTimestampMap: Map<string, string>,
) => {
	githubMetadataResponse.forEach((element: any) => {
		if (
			element.status === OK_STATUS &&
			element.body.meta.access === GRANTED &&
			edges.length < RECENT_ACTIVITIES_LIMIT
		) {
			const originalTimestamp = githubLinkTimestampMap.get(element.body.data.url);
			const currentTimestamp = new Date().toISOString();
			const githubEdge: ActivityItemEdge = {
				node: {
					object: {
						data: {
							displayName: element.body.data.name || element.body.data.title,
						},
						id: element.body.data['@id'],
						type: element.body.meta.resourceType,
						product: 'github' as ActivityProduct,
					},
					event: {
						eventType: 'viewed' as ActivityEventType,
						timestamp: originalTimestamp || currentTimestamp,
					},
				},
				cursor: '',
			};
			edges.push(githubEdge);
		}
	});

	sortEdgesByTimestamp(edges);
	limitToMostRecentActivities(edges, RECENT_ACTIVITIES_LIMIT);
};

const sortEdgesByTimestamp = (edges: ActivityItemEdge[]) => {
	return edges.sort((a, b) => {
		const timeA = new Date(a.node.event.timestamp).getTime();
		const timeB = new Date(b.node.event.timestamp).getTime();
		return timeB - timeA; // Descending order
	});
};

const limitToMostRecentActivities = (
	edges: ActivityItemEdge[],
	limit: number = 200,
): ActivityItemEdge[] => {
	return edges.slice(0, limit);
};

const isJiraResult = (ari: string) => {
	return ari.includes(':jira:');
};

const isConfluenceResult = (ari: string) => {
	return ari.includes(':confluence:');
};
